import { round } from 'lodash';

import {
    DEFAULT_HOURS_OF_OPERATIONS,
    PlaceCategoryTypes,
} from '@barboxx/types';

import '@react-google-maps/api';

import { format, parse } from 'date-fns';

import type { Place } from '@barboxx/models/types';

type PlaceTypes = 'bar' | 'night_club' | 'restaurant';
const TYPE_TO_PLACE_CATEGORY: Record<PlaceTypes, PlaceCategoryTypes> = {
    bar: 'Bar',
    night_club: 'Club',
    restaurant: 'Restaurant',
};

export const formatAddressComponents = (
    data: google.maps.places.PlaceResult['address_components']
) => {
    const extractType = (type: string) =>
        data?.find(d => d.types.includes(type));

    const streetAddress = `${extractType('street_number')?.short_name ?? ''} ${
        extractType('route')?.short_name ?? ''
    }`;
    const zip = extractType('postal_code')?.short_name ?? '';
    const state = extractType('administrative_area_level_1')?.short_name ?? '';
    const city = extractType('locality')?.short_name ?? '';

    return {
        streetAddress,
        zip,
        state,
        city,
    };
};

type DayKey = keyof typeof DEFAULT_HOURS_OF_OPERATIONS;

const GOOGLE_DAY_TO_DAY: Record<number, DayKey> = {
    0: 'sunday',
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday',
};

const googleTimeToMyTime = (time: string) =>
    format(parse(time, 'HHmm', new Date()), 'HH:mm:ss');

export const extractPlaceInfo = (
    data: google.maps.places.PlaceResult
): Place.CreateRequest => {
    // Generated by Copilot
    const { name = '', formatted_phone_number, website = null } = data;
    const { lat = 0, lng = 0 } = data.geometry?.location?.toJSON() ?? {};
    const type = data.types?.find(t =>
        ['bar', 'night_club', 'restaurant'].includes(t)
    ) as PlaceTypes;

    const category = TYPE_TO_PLACE_CATEGORY[type];
    const hoursOfOperations =
        data.opening_hours?.periods?.reduce(
            (prev, curr) => {
                const day = GOOGLE_DAY_TO_DAY[curr.open.day];

                if (!day) {
                    return prev;
                }

                const dayHours = curr.close
                    ? {
                          open: googleTimeToMyTime(curr.open.time),
                          close: googleTimeToMyTime(curr.close.time),
                      }
                    : null;

                return {
                    ...prev,
                    [day]: dayHours,
                };
            },
            { ...DEFAULT_HOURS_OF_OPERATIONS }
        ) ?? DEFAULT_HOURS_OF_OPERATIONS;

    return {
        name: name?.trim(),
        latitude: round(lat, 8),
        longitude: round(lng, 8),
        category,
        website,
        phoneNumber: formatted_phone_number,
        mainImage: null,
        description: null,
        enabled: true,
        hoursOfOperations,
        ...formatAddressComponents(data.address_components),
    };
};
